import { createSignal, For, Show, onMount, onCleanup, createMemo } from "solid-js";
import { useUserContext } from "./UserContext";
import socket from "./socket";
import { useI18n } from "./i18n";
import { ConventionDetails } from "./Conventions";

interface Convention {
  id: number;
  year: number;
  status: string;
  fullname?: string;
  username: string;
  promo_id: number;
  info?: any;
}

interface Promo {
  id: number;
  name: string;
  conventions: Convention[];
  validators?: any[];
}

export default function TeacherView() {
  const t = useI18n();
  const [userStore, setUserStore] = useUserContext();
  const [promosWithConventions, setPromosWithConventions] = createSignal<Promo[]>([]);
  
  const [loading, setLoading] = createSignal(true);
  
  // If we have cached data, use it immediately
  if (userStore.cachedTeacherConventions) {
    console.log("Using cached teacher conventions data");
    setPromosWithConventions(userStore.cachedTeacherConventions);
    setLoading(false);
  }
  
  // Handler for teacher-conventions events
  const handleTeacherConventions = (data: Promo[]) => {
    console.log("Received teacher conventions data:", data);
    setPromosWithConventions(data);
    setLoading(false);
    
    // Cache the data for future use
    setUserStore('cachedTeacherConventions', data);
  };
  
  onMount(() => {
    console.log("TeacherView mounted, requesting data...");
    
    // Request data from the server
    socket.emit("teacher-request-conventions");
    
    // If we don't have cached data, show loading indicator
    if (!userStore.cachedTeacherConventions) {
      setLoading(true);
    }
    
    // Always register the handler (and remove old one if it exists)
    socket.off("teacher-conventions", handleTeacherConventions);
    socket.on("teacher-conventions", handleTeacherConventions);
    
    // Cleanup when component unmounts
    onCleanup(() => {
      console.log("TeacherView unmounting, cleaning up socket handler");
      socket.off("teacher-conventions", handleTeacherConventions);
    });
  });
  
  
  return (
    <div class="container mt-4">
      <h1 class="mb-4">{t("Teacher Dashboard")}</h1>
      
      <Show 
        when={!loading()} 
        fallback={<div class="text-center"><div class="spinner-border" role="status"></div></div>}
      >
        <Show
          when={promosWithConventions().length > 0}
          fallback={<p>{t("You are not assigned as a validator for any class yet.")}</p>}
        >
        <For each={promosWithConventions()}>
          {(promo) => (<PromoView promo={promo} />)}
        </For>
        </Show>
      </Show>
    </div>
  );
}

function PromoView(props: { promo: Promo }) {
  
  const t = useI18n();
  const [userStore] = useUserContext();
  
  const [selectedConvention, setSelectedConvention] = createSignal<Convention | null>(null);
  const [editingFeedback, setEditingFeedback] = createSignal({ conventionId: null as number | null, feedback: "" });

  
  const startEditingFeedback = (convention: Convention) => {
    setEditingFeedback({
      conventionId: convention.id,
      feedback: convention.info.teacherFeedback || ""
    });
  };
  
  const saveFeedback = (sendNotification = false) => {
    const { conventionId, feedback } = editingFeedback();
    
    if (sendNotification) {
      if (!confirm(t("The convention will return to draft mode for the student, and they will receive a notification. Continue?"))) {
        return;
      }
    }
    
    socket.emit("teacher-update-convention-feedback", {
      conventionId,
      feedback,
      sendNotification
    });
    
    setEditingFeedback({ conventionId: null, feedback: "" });
  };
  
  const cancelEditingFeedback = () => {
    setEditingFeedback({ conventionId: null, feedback: "" });
  };
  
  const openConventionModal = (convention: Convention) => {
    setSelectedConvention(convention);
  };
  
  const closeConventionModal = () => {
    setSelectedConvention(null);
    setEditingFeedback({ conventionId: null, feedback: "" });
  };

  // Check if the current teacher has validated this convention
  const hasValidated = (convention: Convention) => {
    if (!convention.info.validations) return false;
    return convention.info.validations.some((v: any) => v.validatorId === userStore.id);
  };
  
  // Get validators that haven't yet validated
  const getRemainingValidators = (convention: Convention, promo: Promo) => {
    if (!convention.info.validations) return promo.validators || [];
    
    const validatedIds = convention.info.validations.map((v: any) => v.validatorId);
    return (promo.validators || []).filter((v: any) => !validatedIds.includes(v.id));
  };

  
  const validateConvention = (conventionId: number) => {
    socket.emit("teacher-validate-convention", conventionId);
  };
  

  const sendExistingFeedbackNotification = (conventionId: number) => {
    if (confirm(t("The convention will return to draft mode for the student, and they will receive a notification. Continue?"))) {
      socket.emit("teacher-send-existing-feedback-notification", {
        conventionId
      });
    }
  };

  // Separate conventions by status
  const validatedConventions = createMemo(() => {
    console.log('all conventions:', props.promo.conventions); 
    return props.promo.conventions.filter((c: Convention) => c.info.fullyValidated && c.status !== 'locked');
  });
  const pendingConventions = createMemo(() => 
    props.promo.conventions.filter((c: Convention) => !c.info.fullyValidated && c.status !== 'locked' && c.status !== 'active')
  );
  const lockedConventions = createMemo(() => 
    props.promo.conventions.filter((c: Convention) => c.status === 'locked')
  );
  const draftWithFeedback = createMemo(() => 
    props.promo.conventions.filter((c: Convention) => 
      c.status === 'active' && 
      c.info.feedbackHistory && 
      c.info.feedbackHistory.length > 0
    )
  );
  
  return (
    <div class="mb-5">
      <h2 class="border-bottom pb-2">{props.promo.name}</h2>
      
      <Show when={pendingConventions().length > 0 || validatedConventions().length > 0 || lockedConventions().length > 0 || draftWithFeedback().length > 0}
            fallback={<p>{t("No internship agreements in this class yet.")}</p>}>
        
        {/* Pending Conventions Section - Compact Table */}
        <Show when={pendingConventions().length > 0}>
          <h4 class="mt-4 mb-3">{t("Pending Agreements")} ({pendingConventions().length})</h4>
          <div class="table-responsive">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th>{t("Student")}</th>
                  <th>{t("Title")}</th>
                  <th>{t("Business")}</th>
                  <th>{t("Status")}</th>
                  <th>{t("Actions")}</th>
                </tr>
              </thead>
              <tbody>
                <For each={pendingConventions()}>
                  {(convention) => (
                    <tr>
                      <td>
                        {convention.fullname ? (
                          <>{convention.fullname}<br/><small class="text-muted">{convention.username}</small></>
                        ) : (
                          convention.username
                        )}
                      </td>
                      <td>{convention.info?.title || t("Internship Agreement")}</td>
                      <td>{convention.info?.business || t("Not specified")}</td>
                      <td>
                        <Show when={hasValidated(convention)} fallback={
                          <span class="badge bg-warning text-dark">{t("Awaiting validation")}</span>
                        }>
                          <span class="badge bg-success">{t("Validated by you")}</span>
                        </Show>
                        <Show when={convention.info?.feedbackHistory && convention.info.feedbackHistory.length > 0}>
                          <br/><span class="badge bg-info mt-1">{t("Resubmitted")}</span>
                        </Show>
                      </td>
                      <td>
                        <button
                          type="button"
                          class="btn btn-sm btn-primary"
                          onClick={() => openConventionModal(convention)}
                        >
                          <i class="fas fa-edit me-1"></i>
                          {t("Process")}
                        </button>
                      </td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        </Show>
        
        {/* Validated Conventions Section - Compact Table */}
        <Show when={validatedConventions().length > 0}>
          <h4 class="mt-4 mb-3">{t("Validated Agreements")} ({validatedConventions().length})</h4>
          <div class="table-responsive">
            <table class="table table-hover table-sm">
              <thead>
                <tr>
                  <th>{t("Student")}</th>
                  <th>{t("Title")}</th>
                  <th>{t("Business")}</th>
                  <th>{t("Actions")}</th>
                </tr>
              </thead>
              <tbody>
                <For each={validatedConventions()}>
                  {(convention) => (
                    <tr>
                      <td>
                        {convention.fullname ? (
                          <>{convention.fullname}<br/><small class="text-muted">{convention.username}</small></>
                        ) : (
                          convention.username
                        )}
                      </td>
                      <td>{convention.info?.title || t("Internship Agreement")}</td>
                      <td>{convention.info?.business || t("Not specified")}</td>
                      <td>
                        <button
                          type="button"
                          class="btn btn-sm btn-outline-secondary"
                          onClick={() => openConventionModal(convention)}
                        >
                          <i class="fas fa-eye me-1"></i>
                          {t("View")}
                        </button>
                      </td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        </Show>
        
        {/* Locked Conventions Section - Compact Table */}
        <Show when={lockedConventions().length > 0}>
          <h4 class="mt-4 mb-3">{t("Locked Agreements")} ({lockedConventions().length})</h4>
          <div class="alert alert-info">
            <i class="fas fa-lock me-2"></i>
            {t("These agreements have been locked by an administrator and cannot be modified.")}
          </div>
          <div class="table-responsive">
            <table class="table table-hover table-sm">
              <thead>
                <tr>
                  <th>{t("Student")}</th>
                  <th>{t("Title")}</th>
                  <th>{t("Business")}</th>
                  <th>{t("Actions")}</th>
                </tr>
              </thead>
              <tbody>
                <For each={lockedConventions()}>
                  {(convention) => (
                    <tr class="table-danger">
                      <td>
                        {convention.fullname ? (
                          <>{convention.fullname}<br/><small class="text-muted">{convention.username}</small></>
                        ) : (
                          convention.username
                        )}
                      </td>
                      <td>
                        {convention.info?.title || t("Internship Agreement")}
                        <br/><span class="badge bg-danger">{t("Locked")}</span>
                      </td>
                      <td>{convention.info?.business || t("Not specified")}</td>
                      <td>
                        <button
                          type="button"
                          class="btn btn-sm btn-outline-secondary"
                          onClick={() => openConventionModal(convention)}
                        >
                          <i class="fas fa-eye me-1"></i>
                          {t("View")}
                        </button>
                      </td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        </Show>
        
        {/* Draft with Feedback Section - Compact Table */}
        <Show when={draftWithFeedback().length > 0}>
          <h4 class="mt-4 mb-3">{t("Returned for Revision")} ({draftWithFeedback().length})</h4>
          <div class="alert alert-warning">
            <i class="fas fa-edit me-2"></i>
            {t("These agreements were returned to the student with feedback and are being revised.")}
          </div>
          <div class="table-responsive">
            <table class="table table-hover table-sm">
              <thead>
                <tr>
                  <th>{t("Student")}</th>
                  <th>{t("Title")}</th>
                  <th>{t("Last Feedback")}</th>
                  <th>{t("Actions")}</th>
                </tr>
              </thead>
              <tbody>
                <For each={draftWithFeedback()}>
                  {(convention) => (
                    <tr class="table-warning">
                      <td>
                        {convention.fullname ? (
                          <>{convention.fullname}<br/><small class="text-muted">{convention.username}</small></>
                        ) : (
                          convention.username
                        )}
                      </td>
                      <td>
                        {convention.info?.title || t("Internship Agreement")}
                        <br/><span class="badge bg-warning text-dark">{t("In Revision")}</span>
                      </td>
                      <td>
                        <Show when={convention.info?.feedbackHistory && convention.info.feedbackHistory.length > 0}>
                          <small class="text-muted">
                            {t("by")} {convention.info.feedbackHistory[0].teacher}<br/>
                            {new Date(convention.info.feedbackHistory[0].date).toLocaleDateString()}
                          </small>
                        </Show>
                      </td>
                      <td>
                        <button
                          type="button"
                          class="btn btn-sm btn-outline-secondary"
                          onClick={() => openConventionModal(convention)}
                        >
                          <i class="fas fa-eye me-1"></i>
                          {t("View")}
                        </button>
                      </td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        </Show>
      </Show>
      
      {/* Convention Details Modal */}
      <Show when={selectedConvention()}>
        <ConventionModal
          convention={selectedConvention()!}
          promo={props.promo}
          onClose={closeConventionModal}
          hasValidated={hasValidated(selectedConvention()!)}
          getRemainingValidators={() => getRemainingValidators(selectedConvention()!, props.promo)}
          onValidate={validateConvention}
          editingFeedback={editingFeedback()}
          onStartEditingFeedback={() => startEditingFeedback(selectedConvention()!)}
          onSaveFeedback={saveFeedback}
          onCancelEditingFeedback={cancelEditingFeedback}
          onSendExistingFeedback={sendExistingFeedbackNotification}
          setEditingFeedback={setEditingFeedback}
          userStore={userStore}
        />
      </Show>
    </div>
  );
}

// Convention Modal Component
function ConventionModal(props: {
  convention: Convention;
  promo: Promo;
  onClose: () => void;
  hasValidated: boolean;
  getRemainingValidators: () => any[];
  onValidate: (id: number) => void;
  editingFeedback: { conventionId: number | null; feedback: string };
  onStartEditingFeedback: () => void;
  onSaveFeedback: (sendNotification: boolean) => void;
  onCancelEditingFeedback: () => void;
  onSendExistingFeedback: (id: number) => void;
  setEditingFeedback: (value: { conventionId: number | null; feedback: string }) => void;
  userStore: any;
}) {
  const t = useI18n();
  
  return (
    <div class="modal show d-block" style="background-color: rgba(0,0,0,0.5);" onClick={props.onClose}>
      <div class="modal-dialog modal-xl modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {props.convention.info?.title || `${t("Internship Agreement")} (${props.convention.year})`}
              <Show when={props.convention.status === 'locked'}>
                <span class="badge bg-danger ms-2">{t("Locked")}</span>
              </Show>
              <Show when={props.convention.status === 'active'}>
                <span class="badge bg-warning text-dark ms-2">{t("In Revision")}</span>
              </Show>
            </h5>
            <button type="button" class="btn-close" onClick={props.onClose}></button>
          </div>
          <div class="modal-body">
            {/* Student Info */}
            <div class="mb-3">
              <h6 class="text-muted">
                {t("Student")}: {props.convention.fullname ? (
                  <>{props.convention.fullname} <small>({props.convention.username})</small></>
                ) : (
                  props.convention.username
                )}
              </h6>
            </div>
            
            {/* Convention Details */}
            <ConventionDetails 
              convention={props.convention} 
              startEditing={() => {}}
              conventionStructure={props.userStore.conventionStructure} 
            />
            
            {/* Validation Status */}
            <Show when={props.convention.status !== 'active'}>
              <div class="mb-3 mt-4">
                <h6>{t("Validation Status")}:</h6>
                <Show when={props.convention.info.validations && props.convention.info.validations.length > 0}>
                  <div class="mt-2">
                    <div>{t("Validated by")}:</div>
                    <ul>
                      <For each={props.convention.info.validations}>
                        {(validation: any) => (
                          <li>
                            {validation.validatorName} - {new Date(validation.timestamp).toLocaleString()}
                          </li>
                        )}
                      </For>
                    </ul>
                  </div>
                </Show>
                <Show when={!props.convention.info.validations || props.convention.info.validations.length === 0}>
                  <span>{t("Not validated yet")}</span>
                </Show>
              </div>
              
              {/* Remaining validators */}
              <Show when={props.getRemainingValidators().length > 0}>
                <div class="mb-3">
                  <strong>{t("Waiting for validation from")}:</strong>
                  <ul class="mt-1">
                    <For each={props.getRemainingValidators()}>
                      {(validator: any) => (
                        <li>
                          {validator.fullname ? (
                            <>{validator.fullname} <small class="text-muted">({validator.username})</small></>
                          ) : (
                            validator.username
                          )}
                        </li>
                      )}
                    </For>
                  </ul>
                </div>
              </Show>
            </Show>
            
            {/* Teacher Feedback - Only for pending conventions */}
            <Show when={props.convention.status === 'pending' || props.convention.status === 'validated'}>
              <div class="mb-3 mt-4">
                <h6>{t("Teacher Feedback")}:</h6>
                <Show when={props.convention.info?.feedbackHistory && props.convention.info.feedbackHistory.length > 0}>
                  <span class="badge bg-info ms-2">{t("Resubmitted")}</span>
                  <div class="alert alert-info mt-2">
                    <small>{t("This convention has been resubmitted after receiving feedback.")}{' '}
                    {t("Previous feedback can be found below.")}</small>
                  </div>
                </Show>
                <Show
                  when={props.editingFeedback.conventionId !== props.convention.id}
                  fallback={
                    <div class="mt-2">
                      <textarea
                        class="form-control mb-2"
                        rows={5}
                        value={props.editingFeedback.feedback}
                        onInput={(e) => props.setEditingFeedback({
                          ...props.editingFeedback,
                          feedback: e.currentTarget.value
                        })}
                      ></textarea>
                      
                      <div class="d-flex gap-2">
                        <button 
                          type="button" 
                          class="btn btn-primary"
                          onClick={() => props.onSaveFeedback(false)}
                          title={t("Save without sending notification to student")}
                        >
                          {t("Save Draft")}
                        </button>
                        
                        <button 
                          type="button" 
                          class="btn btn-success"
                          onClick={() => props.onSaveFeedback(true)}
                          title={t("Save and send notification to student")}
                        >
                          <i class="fas fa-paper-plane me-1"></i>
                          {t("Save & Send Notification")}
                        </button>
                        
                        <button 
                          type="button" 
                          class="btn btn-secondary"
                          onClick={props.onCancelEditingFeedback}
                        >
                          {t("Cancel")}
                        </button>
                      </div>
                    </div>
                  }
                >
                  <div class="d-flex align-items-center">
                    <div class="me-auto">
                      {props.convention.info?.teacherFeedback ? (
                        <div>
                          <p class="mb-0" style="white-space: pre-wrap">{props.convention.info.teacherFeedback}</p>
                          <small class="text-muted">
                            {t("Last updated")}: {new Date(props.convention.info.feedbackDate).toLocaleString()}
                          </small>
                        </div>
                      ) : (
                        t("No feedback draft")
                      )}
                    </div>
                    
                    <div class="btn-group" role="group">
                      <button 
                        type="button" 
                        class="btn btn-sm btn-primary"
                        onClick={props.onStartEditingFeedback}
                      >
                        {props.convention.info?.teacherFeedback ? t("Edit Feedback") : t("Add Feedback")}
                      </button>
                      
                      <Show when={props.convention.info?.teacherFeedback}>
                        <button 
                          type="button" 
                          class="btn btn-sm btn-success"
                          onClick={() => props.onSendExistingFeedback(props.convention.id)}
                          title={t("Send notification to student about this feedback")}
                        >
                          <i class="fas fa-paper-plane"></i>
                          {t("Send Notification")}
                        </button>
                      </Show>
                    </div>
                  </div>
                </Show>
              </div>
              
              {/* Feedback History */}
              <Show when={props.convention.info?.feedbackHistory && props.convention.info.feedbackHistory.length > 0}>
                <div class="mb-3">
                  <strong>{t("Feedback History")}:</strong>
                  <div class="card mt-2">
                    <div class="card-body p-0">
                      <div class="list-group list-group-flush">
                        <For each={props.convention.info.feedbackHistory}>
                          {(historyItem: any) => (
                            <div class="list-group-item">
                              <div class="d-flex justify-content-between align-items-center mb-1">
                                <strong>{t("Version")} {historyItem.conventionVersion || '?'}</strong>
                                <small class="text-muted">
                                  {t("by")} {historyItem.teacher} {t("on")} {new Date(historyItem.date).toLocaleString()}
                                </small>
                              </div>
                              <p class="mb-1" style="white-space: pre-wrap">{historyItem.feedback}</p>
                              <div class="d-flex gap-2">
                                <Show when={historyItem.notificationSent}>
                                  <small class="badge bg-info">
                                    <i class="fas fa-envelope me-1"></i> {t("Notification sent")}
                                  </small>
                                </Show>
                                <Show when={historyItem.validations && historyItem.validations.length > 0}>
                                  <small class="text-muted">
                                    {t("Validated by")} {historyItem.validations.length} {t("teacher(s)")}
                                  </small>
                                </Show>
                              </div>
                            </div>
                          )}
                        </For>
                      </div>
                    </div>
                  </div>
                </div>
              </Show>
            </Show>
            
            {/* For draft with feedback - show feedback history */}
            <Show when={props.convention.status === 'active' && props.convention.info?.feedbackHistory}>
              <div class="mb-3 mt-4">
                <h6>{t("Feedback History")}:</h6>
                <For each={props.convention.info.feedbackHistory}>
                  {(historyItem: any, index) => (
                    <div class="card mt-2">
                      <div class="card-body">
                        <div class="d-flex justify-content-between align-items-center mb-2">
                          <strong>
                            {index() === 0 ? t("Last Feedback Sent") : `${t("Version")} ${historyItem.conventionVersion || '?'}`}
                          </strong>
                          <small class="text-muted">
                            {t("by")} {historyItem.teacher} {t("on")} {new Date(historyItem.date).toLocaleString()}
                          </small>
                        </div>
                        <p class="mb-1" style="white-space: pre-wrap">{historyItem.feedback}</p>
                        <Show when={historyItem.validations && historyItem.validations.length > 0}>
                          <small class="text-muted">
                            {t("Validated by")} {historyItem.validations.length} {t("teacher(s)")}
                          </small>
                        </Show>
                      </div>
                    </div>
                  )}
                </For>
              </div>
            </Show>
          </div>
          <div class="modal-footer">
            <Show when={!props.hasValidated && props.convention.status === 'pending'}>
              <button 
                type="button" 
                class="btn btn-success"
                onClick={() => {
                  props.onValidate(props.convention.id);
                  props.onClose();
                }}
              >
                <i class="fas fa-check me-1"></i>
                {t("Validate")}
              </button>
            </Show>
            <button type="button" class="btn btn-secondary" onClick={props.onClose}>
              {t("Close")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}